import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  TrendingUp, TrendingDown, DollarSign, Receipt, Package,
  Percent, ChevronLeft, ChevronRight, Loader2, Calendar,
  FileText, AlertCircle, Minus,
} from 'lucide-react';

interface PnlData {
  month: number;
  year: number;
  grossRevenue: number;
  grossDiscount: number;
  totalCogs: number;
  totalCommission: number;
  grossProfit: number;
  totalExpenses: number;
  trueNetProfit: number;
  expenseCount: number;
  expenses: Array<{
    id: number;
    category: string;
    amount: number;
    description: string | null;
    expenseDate: string;
  }>;
}

interface FinanceDashboardProps {
  storeApiKey: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FinanceDashboard: React.FC<FinanceDashboardProps> = ({ storeApiKey }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState<PnlData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchPnl = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/reports/monthly-pnl?month=${month}&year=${year}`, {
        headers: { 'x-store-api-key': storeApiKey },
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Failed to load P&L'); return; }
      setData(d);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }, [month, year, storeApiKey]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else { setMonth(m => m - 1); } };
  const next = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else { setMonth(m => m + 1); } };

  const isCurrent = month === now.getMonth() + 1 && year === now.getFullYear();

  const KpiCard = ({ label, value, icon: Icon, color, prefix = 'Rs.' }: { label: string; value: number; icon: any; color: string; prefix?: string }) => (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-200 shadow-sm">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white shrink-0 shadow-md`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-[16px] font-black text-slate-800 font-mono tracking-tight truncate">
          {prefix} {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header with month picker */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg shadow-emerald-200 flex items-center justify-center">
            <TrendingUp size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-[15px] font-black text-slate-800">Finance & P&L</h2>
            <p className="text-[10px] text-slate-500">Monthly Profit & Loss Analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prev} className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors">
            <ChevronLeft size={14} />
          </button>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <span className="text-[11px] font-black text-slate-700 font-mono">{MONTHS[month - 1]} {year}</span>
            {isCurrent && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[8px] font-black uppercase">Current</span>}
          </div>
          <button onClick={next} disabled={isCurrent} className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors disabled:opacity-30">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-[11px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading P&L…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-medium">
            <AlertCircle size={13} /> {error}
          </div>
        )}

        {!loading && !error && !data && (
          <div className="text-center py-12 text-slate-400 text-[11px]">
            <TrendingUp size={28} className="mx-auto mb-2 text-slate-300" />
            No data available for this period.
          </div>
        )}

        {data && (
          <>
            {/* KPI Grid: Revenue */}
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Revenue & COGS</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <KpiCard label="Gross Revenue" value={data.grossRevenue} icon={DollarSign} color="bg-gradient-to-br from-emerald-400 to-teal-500" />
                <KpiCard label="Total COGS" value={data.totalCogs} icon={Package} color="bg-gradient-to-br from-amber-400 to-orange-500" />
                <KpiCard label="Discounts" value={data.grossDiscount} icon={Percent} color="bg-gradient-to-br from-sky-400 to-blue-500" />
                <KpiCard label="Commissions" value={data.totalCommission} icon={Minus} color="bg-gradient-to-br from-violet-400 to-purple-500" />
              </div>
            </div>

            {/* Gross Profit */}
            <div className="px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">Gross Profit (Revenue - COGS - Discounts - Commissions)</p>
                <p className="text-[18px] font-black text-emerald-800 font-mono">Rs. {data.grossProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>

            {/* KPI Grid: Expenses */}
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Expenses</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <KpiCard label="Total Expenses" value={data.totalExpenses} icon={Receipt} color="bg-gradient-to-br from-rose-400 to-pink-500" />
                <KpiCard label="Expense Items" value={data.expenseCount} icon={FileText} color="bg-gradient-to-br from-slate-400 to-slate-500" prefix="" />
              </div>
            </div>

            {/* True Net Profit */}
            <div className={`px-4 py-4 rounded-2xl border shadow-sm ${
              data.trueNetProfit >= 0
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">True Net Profit</p>
                  <p className="text-[10px] text-slate-500">Gross Profit − Total Expenses</p>
                </div>
                <div className="flex items-center gap-2">
                  {data.trueNetProfit >= 0
                    ? <TrendingUp size={18} className="text-emerald-600" />
                    : <TrendingDown size={18} className="text-red-600" />
                  }
                  <p className={`text-[22px] font-black font-mono ${
                    data.trueNetProfit >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    Rs. {data.trueNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Expense Breakdown */}
            {data.expenses.length > 0 && (
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Expense Breakdown</p>
                <div className="space-y-1.5">
                  {data.expenses.map(e => (
                    <div key={e.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/70 backdrop-blur-sm border border-slate-200">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-slate-700">{e.category}</span>
                          <span className="text-[9px] text-slate-400 font-mono">{e.expenseDate}</span>
                        </div>
                        {e.description && (
                          <p className="text-[9px] text-slate-400 truncate mt-0.5">{e.description}</p>
                        )}
                      </div>
                      <span className="text-[12px] font-black font-mono text-rose-600 shrink-0 ml-3">
                        -Rs. {Number(e.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}


          </>
        )}
      </div>
    </div>
  );
};

export default FinanceDashboard;
